import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import SettingsSection from './components/SettingsSection';
import SettingToggle from './components/SettingToggle';
import ServiceStatus from './components/ServiceStatus';
import DataUsageCard from './components/DataUsageCard';
import ImportExportCard from './components/ImportExportCard';
import AccountSection from './components/AccountSection';

const Settings = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    notifications: true,
    messaging: true,
    account: false,
    privacy: false,
    importExport: false,
    appearance: false,
    advanced: false
  });

  // Settings state
  const [settings, setSettings] = useState({
    // Notification settings
    birthdayReminders: true,
    randomContactPrompts: true,
    deliveryConfirmations: true,
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    reminderTime: '09:00',
    reminderDays: '1',
    
    // Messaging preferences
    defaultTemplate: 'birthday_casual',
    sendingSchedule: 'immediate',
    channelPriority: 'email',
    autoPersonalization: true,
    messageHistory: true,
    
    // Privacy settings
    dataSharing: false,
    analyticsTracking: true,
    backupFrequency: 'weekly',
    
    // Appearance
    theme: 'system',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    timezone: 'America/New_York'
  });

  const [serviceStatuses] = useState({
    email: { status: 'connected', lastSync: '2 hours ago' },
    sms: { status: 'error', lastSync: null },
    whatsapp: { status: 'warning', lastSync: '1 day ago' },
    calendar: { status: 'connected', lastSync: '30 minutes ago' }
  });

  const [dataUsage] = useState({
    contacts: { value: 1247, unit: 'contacts', percentage: 62, trend: 5 },
    storage: { value: 2.4, unit: 'GB', percentage: 24, trend: -2 },
    messages: { value: 3892, unit: 'sent', percentage: null, trend: 12 },
    backups: { value: 15, unit: 'backups', percentage: null, trend: 0 }
  });

  const templateOptions = [
    { value: 'birthday_casual', label: 'Casual Birthday Wishes' },
    { value: 'birthday_formal', label: 'Formal Birthday Greetings' },
    { value: 'birthday_funny', label: 'Funny Birthday Messages' },
    { value: 'custom', label: 'Custom Template' }
  ];

  const scheduleOptions = [
    { value: 'immediate', label: 'Send Immediately' },
    { value: 'morning', label: 'Send in Morning (9 AM)' },
    { value: 'evening', label: 'Send in Evening (6 PM)' },
    { value: 'custom', label: 'Custom Time' }
  ];

  const channelOptions = [
    { value: 'email', label: 'Email First' },
    { value: 'sms', label: 'SMS First' },
    { value: 'whatsapp', label: 'WhatsApp First' },
    { value: 'auto', label: 'Auto Select' }
  ];

  const themeOptions = [
    { value: 'light', label: 'Light Mode' },
    { value: 'dark', label: 'Dark Mode' },
    { value: 'system', label: 'System Preference' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' }
  ];

  const backupOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'manual', label: 'Manual Only' }
  ];

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('contactsync_settings');
    if (savedSettings) {
      setSettings({ ...settings, ...JSON.parse(savedSettings) });
    }
  }, []);

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('contactsync_settings', JSON.stringify(newSettings));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleServiceConfigure = (service) => {
    console.log(`Configuring ${service} service`);
    // Navigate to service configuration
  };

  const handleServiceTest = async (service) => {
    console.log(`Testing ${service} service`);
    // Test service connection
  };

  const handleImport = async (file) => {
    console.log('Importing file:', file?.name);
    // Handle file import
  };

  const handleExport = async () => {
    console.log('Exporting data');
    // Handle data export
  };

  const handleBackup = async () => {
    console.log('Creating backup');
    // Handle backup creation
  };

  const handleUpdateProfile = async (profileData) => {
    console.log('Updating profile:', profileData);
    // Handle profile update
  };

  const handleChangePassword = async (passwordData) => {
    console.log('Changing password');
    // Handle password change
  };

  const handleResetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default? This action cannot be undone.')) {
      localStorage.removeItem('contactsync_settings');
      window.location?.reload();
    }
  };

  const filteredSections = searchQuery ? 
    Object.keys(expandedSections)?.filter(section => 
      section?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    ) : Object.keys(expandedSections);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <BottomTabNavigation className="hidden lg:flex" />
      {/* Main Content */}
      <div className="lg:ml-60">
        {/* Navigation Breadcrumbs */}
        <NavigationBreadcrumbs />

        {/* Header */}
        <div className="sticky top-0 z-50 bg-surface border-b border-border shadow-card">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center space-x-3">
              <Icon name="Settings" size={24} className="text-primary" />
              <h1 className="text-xl lg:text-2xl font-bold text-text-primary">Settings</h1>
            </div>
            
            {/* Search */}
            <div className="hidden md:flex flex-1 max-w-md mx-6">
              <div className="relative w-full">
                <Input
                  type="search"
                  placeholder="Search settings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10"
                />
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
                />
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleResetSettings}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Reset All
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-6 pb-20 lg:pb-6">
          <div className="max-w-4xl mx-auto">
            {/* Data Usage Overview */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <DataUsageCard
                title="Total Contacts"
                value={dataUsage?.contacts?.value}
                unit={dataUsage?.contacts?.unit}
                percentage={dataUsage?.contacts?.percentage}
                icon="Users"
                trend={dataUsage?.contacts?.trend}
              />
              <DataUsageCard
                title="Storage Used"
                value={dataUsage?.storage?.value}
                unit={dataUsage?.storage?.unit}
                percentage={dataUsage?.storage?.percentage}
                icon="HardDrive"
                trend={dataUsage?.storage?.trend}
              />
              <DataUsageCard
                title="Messages Sent"
                value={dataUsage?.messages?.value}
                unit={dataUsage?.messages?.unit}
                percentage={null}
                icon="MessageSquare"
                trend={dataUsage?.messages?.trend}
              />
              <DataUsageCard
                title="Backups"
                value={dataUsage?.backups?.value}
                unit={dataUsage?.backups?.unit}
                percentage={null}
                icon="Shield"
                trend={dataUsage?.backups?.trend}
              />
            </div>

            <div className="space-y-6">
              {/* Notifications Section */}
              {(!searchQuery || filteredSections?.includes('notifications')) && (
                <SettingsSection
                  title="Notifications"
                  description="Configure birthday reminders and message alerts"
                  icon="Bell"
                  isExpanded={expandedSections?.notifications}
                  onToggle={() => toggleSection('notifications')}
                >
                  <div className="space-y-4 mt-4">
                    <SettingToggle
                      label="Birthday Reminders"
                      description="Get notified about upcoming birthdays"
                      checked={settings?.birthdayReminders}
                      onChange={(value) => handleSettingChange('birthdayReminders', value)}
                    />
                    
                    <SettingToggle
                      label="Random Contact Prompts"
                      description="Receive suggestions to reach out to contacts"
                      checked={settings?.randomContactPrompts}
                      onChange={(value) => handleSettingChange('randomContactPrompts', value)}
                    />
                    
                    <SettingToggle
                      label="Delivery Confirmations"
                      description="Get notified when messages are delivered"
                      checked={settings?.deliveryConfirmations}
                      onChange={(value) => handleSettingChange('deliveryConfirmations', value)}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
                      <Input
                        label="Reminder Time"
                        type="time"
                        value={settings?.reminderTime}
                        onChange={(e) => handleSettingChange('reminderTime', e?.target?.value)}
                      />
                      
                      <Select
                        label="Days Before Birthday"
                        options={[
                          { value: '0', label: 'Same Day' },
                          { value: '1', label: '1 Day Before' },
                          { value: '2', label: '2 Days Before' },
                          { value: '7', label: '1 Week Before' }
                        ]}
                        value={settings?.reminderDays}
                        onChange={(value) => handleSettingChange('reminderDays', value)}
                      />
                    </div>
                  </div>
                </SettingsSection>
              )}

              {/* Messaging Section */}
              {(!searchQuery || filteredSections?.includes('messaging')) && (
                <SettingsSection
                  title="Messaging Preferences"
                  description="Configure message templates and delivery settings"
                  icon="MessageSquare"
                  isExpanded={expandedSections?.messaging}
                  onToggle={() => toggleSection('messaging')}
                >
                  <div className="space-y-6 mt-4">
                    {/* Service Status */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-text-primary">Service Integrations</h4>
                      <ServiceStatus
                        serviceName="Email Service"
                        status={serviceStatuses?.email?.status}
                        lastSync={serviceStatuses?.email?.lastSync}
                        onConfigure={() => handleServiceConfigure('email')}
                        onTest={() => handleServiceTest('email')}
                      />
                      <ServiceStatus
                        serviceName="SMS Service"
                        status={serviceStatuses?.sms?.status}
                        lastSync={serviceStatuses?.sms?.lastSync}
                        onConfigure={() => handleServiceConfigure('sms')}
                      />
                      <ServiceStatus
                        serviceName="WhatsApp API"
                        status={serviceStatuses?.whatsapp?.status}
                        lastSync={serviceStatuses?.whatsapp?.lastSync}
                        onConfigure={() => handleServiceConfigure('whatsapp')}
                        onTest={() => handleServiceTest('whatsapp')}
                      />
                    </div>

                    {/* Message Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
                      <Select
                        label="Default Template"
                        options={templateOptions}
                        value={settings?.defaultTemplate}
                        onChange={(value) => handleSettingChange('defaultTemplate', value)}
                      />
                      
                      <Select
                        label="Sending Schedule"
                        options={scheduleOptions}
                        value={settings?.sendingSchedule}
                        onChange={(value) => handleSettingChange('sendingSchedule', value)}
                      />
                      
                      <Select
                        label="Channel Priority"
                        options={channelOptions}
                        value={settings?.channelPriority}
                        onChange={(value) => handleSettingChange('channelPriority', value)}
                      />
                    </div>

                    <div className="space-y-4 pt-4 border-t border-border">
                      <SettingToggle
                        label="Auto Personalization"
                        description="Automatically personalize messages with contact details"
                        checked={settings?.autoPersonalization}
                        onChange={(value) => handleSettingChange('autoPersonalization', value)}
                      />
                      
                      <SettingToggle
                        label="Message History"
                        description="Keep track of all sent messages"
                        checked={settings?.messageHistory}
                        onChange={(value) => handleSettingChange('messageHistory', value)}
                      />
                    </div>
                  </div>
                </SettingsSection>
              )}

              {/* Account Section */}
              {(!searchQuery || filteredSections?.includes('account')) && (
                <SettingsSection
                  title="Account Management"
                  description="Manage your profile and subscription"
                  icon="User"
                  isExpanded={expandedSections?.account}
                  onToggle={() => toggleSection('account')}
                >
                  <AccountSection
                    userProfile={{
                      name: "John Smith",
                      email: "john.smith@example.com",
                      phone: "+1 (555) 123-4567",
                      timezone: "America/New_York"
                    }}
                    onUpdateProfile={handleUpdateProfile}
                    onChangePassword={handleChangePassword}
                    className="mt-4"
                  />
                </SettingsSection>
              )}

              {/* Privacy Section */}
              {(!searchQuery || filteredSections?.includes('privacy')) && (
                <SettingsSection
                  title="Privacy & Security"
                  description="Control data sharing and backup settings"
                  icon="Shield"
                  isExpanded={expandedSections?.privacy}
                  onToggle={() => toggleSection('privacy')}
                >
                  <div className="space-y-4 mt-4">
                    <SettingToggle
                      label="Data Sharing"
                      description="Allow anonymous usage data to improve the service"
                      checked={settings?.dataSharing}
                      onChange={(value) => handleSettingChange('dataSharing', value)}
                    />
                    
                    <SettingToggle
                      label="Analytics Tracking"
                      description="Enable analytics to help us improve your experience"
                      checked={settings?.analyticsTracking}
                      onChange={(value) => handleSettingChange('analyticsTracking', value)}
                    />

                    <div className="pt-4 border-t border-border">
                      <Select
                        label="Backup Frequency"
                        description="How often to automatically backup your data"
                        options={backupOptions}
                        value={settings?.backupFrequency}
                        onChange={(value) => handleSettingChange('backupFrequency', value)}
                      />
                    </div>
                  </div>
                </SettingsSection>
              )}

              {/* Import/Export Section */}
              {(!searchQuery || filteredSections?.includes('importExport')) && (
                <SettingsSection
                  title="Import & Export"
                  description="Manage your data import and backup options"
                  icon="Database"
                  isExpanded={expandedSections?.importExport}
                  onToggle={() => toggleSection('importExport')}
                >
                  <ImportExportCard
                    onImport={handleImport}
                    onExport={handleExport}
                    onBackup={handleBackup}
                    className="mt-4"
                  />
                </SettingsSection>
              )}

              {/* Appearance Section */}
              {(!searchQuery || filteredSections?.includes('appearance')) && (
                <SettingsSection
                  title="Appearance & Language"
                  description="Customize the look and feel of the application"
                  icon="Palette"
                  isExpanded={expandedSections?.appearance}
                  onToggle={() => toggleSection('appearance')}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Select
                      label="Theme"
                      options={themeOptions}
                      value={settings?.theme}
                      onChange={(value) => handleSettingChange('theme', value)}
                    />
                    
                    <Select
                      label="Language"
                      options={languageOptions}
                      value={settings?.language}
                      onChange={(value) => handleSettingChange('language', value)}
                    />
                    
                    <Input
                      label="Date Format"
                      type="text"
                      value={settings?.dateFormat}
                      onChange={(e) => handleSettingChange('dateFormat', e?.target?.value)}
                      placeholder="MM/DD/YYYY"
                    />
                    
                    <Select
                      label="Time Format"
                      options={[
                        { value: '12h', label: '12 Hour (AM/PM)' },
                        { value: '24h', label: '24 Hour' }
                      ]}
                      value={settings?.timeFormat}
                      onChange={(value) => handleSettingChange('timeFormat', value)}
                    />
                  </div>
                </SettingsSection>
              )}

              {/* Advanced Section */}
              {(!searchQuery || filteredSections?.includes('advanced')) && (
                <SettingsSection
                  title="Advanced Settings"
                  description="Developer and advanced user options"
                  icon="Code"
                  isExpanded={expandedSections?.advanced}
                  onToggle={() => toggleSection('advanced')}
                >
                  <div className="space-y-4 mt-4">
                    <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="AlertTriangle" size={16} className="text-warning" />
                        <span className="text-sm font-medium text-warning">Advanced Options</span>
                      </div>
                      <p className="text-xs text-text-secondary">
                        These settings are for advanced users only. Changing these may affect app functionality.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Download"
                        iconPosition="left"
                        onClick={() => console.log('Export logs')}
                      >
                        Export Debug Logs
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RefreshCw"
                        iconPosition="left"
                        onClick={() => console.log('Clear cache')}
                      >
                        Clear Application Cache
                      </Button>
                      
                      <Button
                        variant="destructive"
                        size="sm"
                        iconName="Trash2"
                        iconPosition="left"
                        onClick={() => {
                          if (window.confirm('This will delete all your data. Are you sure?')) {
                            localStorage.clear();
                            window.location?.reload();
                          }
                        }}
                      >
                        Delete All Data
                      </Button>
                    </div>
                  </div>
                </SettingsSection>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Bottom Navigation */}
      <BottomTabNavigation className="lg:hidden" />
    </div>
  );
};

export default Settings;